<template>
  <div :class="cardClass">
    <!-- Embed Mode (Instagram, LinkedIn, Twitter, etc.) -->
    <div
      v-if="mode === 'embed'"
      class="social-media-card__embed"
    >
      <iframe
        v-if="embedUrl"
        :src="embedUrl"
        width="100%"
        :height="embedIframeHeight"
        :title="embedIframeTitle"
        :allow="embedIframeAllow"
        allowfullscreen
      />
      <slot
        v-else
        name="embed"
      />
    </div>

    <!-- Custom Mode (Manual content) -->
    <template v-else-if="mode === 'custom'">
      <div
        v-if="platform || author"
        class="social-media-card__header"
      >
        <div class="social-media-card__header-top">
          <span
            v-if="platform"
            class="social-media-card__platform-name"
          >{{ platformName }}</span>
        </div>
        <div class="social-media-card__author">
          <img
            :src="avatarSrc"
            :alt="author"
            class="social-media-card__avatar"
            @error="onAvatarError"
          >
          <div class="social-media-card__author-info">
            <p class="social-media-card__author-name">
              {{ author }}
            </p>
            <p
              v-if="date"
              class="social-media-card__date"
            >
              {{ formattedDate }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="showVideo"
        class="social-media-card__videoContainer"
      >
        <slot name="video" />
      </div>

      <div
        v-else-if="image"
        class="social-media-card__imageContainer"
      >
        <c-image
          class="social-media-card__image"
          :image-desktop="image"
          :alt="imageAlt"
        />
      </div>

      <div class="social-media-card__content">
        <c-text
          v-if="hasTitle"
          class="social-media-card__title"
          :color="titleColor"
          size="md"
          weight="bolder"
        >
          <slot name="title" />
        </c-text>

        <c-text
          class="social-media-card__text"
          :class="{ 'social-media-card__text--clamp': textClamp > 0 }"
          :style="textClamp > 0 ? { '--text-clamp': textClamp } : null"
          color="#3b3b3b"
          size="xs"
          weight="normal"
        >
          <slot name="text" />
        </c-text>

        <div
          v-if="hashtags && hashtags.length > 0"
          class="social-media-card__hashtags"
        >
          <span
            v-for="(tag, index) in hashtags"
            :key="index"
            class="social-media-card__hashtag"
          >
            #{{ tag }}
          </span>
        </div>
      </div>

      <div class="social-media-card__footer">
        <a
          v-if="link"
          :href="link"
          :target="linkTarget"
          class="social-media-card__link"
        >
          {{ linkText }}
        </a>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'social-media-card',
  props: {
    mode: {
      type: String,
      default: 'custom',
      validator(value) {
        return ['embed', 'custom', ''].includes(value)
      },
    },
    // Embed mode props
    embedCode: {
      type: String,
      default: '',
    },
    embedUrl: {
      type: String,
      default: '',
    },
    platform: {
      type: String,
      default: '',
      validator(value) {
        return ['', 'linkedin', 'instagram', 'twitter', 'facebook', 'youtube'].includes(value)
      },
    },
    hasVideo: {
      type: Boolean,
      default: false,
    },
    author: {
      type: String,
      default: '',
    },
    authorAvatar: {
      type: String,
      default: '',
    },
    defaultAvatar: {
      type: String,
      default:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'%3E%3Crect width='48' height='48' fill='%23f0f0f0'/%3E%3Ccircle cx='24' cy='18' r='9' fill='%23c4c4c4'/%3E%3Cpath d='M8 44c0-8.837 7.163-16 16-16s16 7.163 16 16' fill='%23c4c4c4'/%3E%3C/svg%3E",
    },
    date: {
      type: String,
      default: '',
    },
    image: {
      type: String,
      default: '',
    },
    imageAlt: {
      type: String,
      default: '',
    },
    titleColor: {
      type: String,
      default: '#1d2822',
    },
    hashtags: {
      type: Array,
      default: () => [],
    },
    link: {
      type: String,
      default: '',
    },
    linkText: {
      type: String,
      default: 'Ver post',
    },
    linkTarget: {
      type: String,
      default: '_blank',
    },
    variant: {
      type: String,
      default: 'default',
      validator(value) {
        return ['', 'default', 'compact', 'featured'].includes(value)
      },
    },
    textClamp: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      avatarError: false,
    }
  },
  computed: {
    avatarSrc() {
      return this.avatarError || !this.authorAvatar
        ? this.defaultAvatar
        : this.authorAvatar
    },
    cardClass() {
      return [
        'social-media-card',
        `social-media-card--${this.variant}`,
        `social-media-card--${this.mode}`,
        {
          'social-media-card--has-image': this.image,
        },
      ]
    },
    hasTitle() {
      return !!this.$slots.title
    },
    showVideo() {
      return this.platform === 'youtube' && this.hasVideo && !!this.$slots.video
    },
    platformName() {
      const names = {
        linkedin: 'LinkedIn',
        instagram: 'Instagram',
        twitter: 'X (Twitter)',
        facebook: 'Facebook',
        youtube: 'YouTube',
      }
      return names[this.platform] || this.platform
    },
    embedIframeTitle() {
      const titles = {
        youtube: 'YouTube video player',
        linkedin: 'LinkedIn post',
        facebook: 'Facebook post',
      }
      return titles[this.platform] || 'Embedded post'
    },
    embedIframeHeight() {
      const heights = {
        youtube: '315',
        linkedin: '570',
        facebook: '570',
      }
      return heights[this.platform] || '400'
    },
    embedIframeAllow() {
      return this.platform === 'youtube'
        ? 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        : ''
    },
    formattedDate() {
      if (!this.date) return ''

      try {
        const date = new Date(this.date)
        const now = new Date()
        const diffTime = Math.abs(now - date)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

        if (diffDays === 0) return 'Hoje'
        if (diffDays === 1) return 'Ontem'
        if (diffDays < 7) return `${diffDays} dias atrás`
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} semanas atrás`
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} meses atrás`

        return date.toLocaleDateString('pt-BR')
      } catch (e) {
        return this.date
      }
    },
  },
  mounted() {
    if (this.mode === 'embed' && this.$slots.embed) {
      this.$nextTick(() => this.loadInstagramEmbed())
    }
  },
  methods: {
    onAvatarError() {
      this.avatarError = true
    },
    loadInstagramEmbed() {
      if (window.instgrm) {
        window.instgrm.Embeds.process()
        return
      }
      const script = document.createElement('script')
      script.async = true
      script.src = '//www.instagram.com/embed.js'
      script.onload = () => {
        if (window.instgrm) window.instgrm.Embeds.process()
      }
      document.body.appendChild(script)
    },
  },
}
</script>

<style lang="scss" scoped>
.social-media-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 25rem;
  min-width: 18.75rem;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  overflow: hidden;

  &__embed {
    width: 100%;
    min-height: 12.5rem;
    display: flex;
    align-items: center;
    justify-content: center;

    // Reset iframe styles
    ::v-deep iframe {
      max-width: 100%;
      border: none;
    }
  }

  &__header {
    padding: 1rem;
    padding-bottom: 0.75rem;
  }

  &__header-top {
    margin-bottom: 0.75rem;
  }

  &__platform-name {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #6a6a6a;
    text-transform: uppercase;
    letter-spacing: 0.0625rem;
  }

  &__author {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
  }

  &__avatar {
    width: 3rem;
    height: 3rem;
    border-radius: 0.25rem;
    object-fit: contain;
    flex-shrink: 0;
    background: #f6f7f8;
    padding: 0.25rem;
  }

  &__author-info {
    flex: 1;
    min-width: 0;
  }

  &__author-name {
    font-size: 0.9375rem;
    font-weight: 700;
    color: #1d2822;
    margin: 0 0 0.125rem 0;
    line-height: 1.3;
  }

  &__date {
    font-size: 0.8125rem;
    color: #6a6a6a;
    margin: 0;
    line-height: 1.3;
  }

  &__imageContainer {
    width: 100%;
    max-width: 25rem;
    max-height: 25rem;
    min-width: 18.75rem;
    margin: 0 auto;
    overflow: hidden;
    background: transparent;
    aspect-ratio: 1 / 1;
    flex-shrink: 0;
  }

  &__videoContainer {
    width: 100%;
    max-width: 25rem;
    max-height: 25rem;
    min-width: 18.75rem;
    margin: 0 auto;
    overflow: hidden;
    background: #000000;
    aspect-ratio: 1 / 1;
    flex-shrink: 0;

    ::v-deep iframe,
    ::v-deep video,
    ::v-deep .vt-video {
      width: 100%;
      height: 100%;
      display: block;
      border: none;
      object-fit: cover;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    display: block;

    ::v-deep picture {
      display: block;
      width: 100%;
      height: 100%;
    }

    ::v-deep img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      transition: transform 0.3s ease;
    }

    .social-media-card:hover & ::v-deep img {
      transform: scale(1.08);
    }
  }

  &__content {
    padding: 1rem 1rem 0.75rem 1rem;
    flex: 1;
  }

  &__title {
    margin-bottom: 0.75rem;
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.4;
    color: #1d2822;
  }

  &__text {
    line-height: 1.5;
    margin-bottom: 0.75rem;
    color: #3b3b3b;
    font-size: 0.9375rem;

    ::v-deep p {
      margin: 0;
    }

    // Limita o texto a N linhas (via prop text-clamp).
    &--clamp {
      display: -webkit-box;
      -webkit-line-clamp: var(--text-clamp, 6);
      line-clamp: var(--text-clamp, 6);
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &__hashtags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin-top: 0.75rem;
  }

  &__hashtag {
    font-size: 0.875rem;
    color: #9ddda9;
    font-weight: 400;
    transition: color 0.2s ease;

    &:hover {
      color: #8a9984;
      text-decoration: underline;
    }
  }

  &__footer {
    padding: 0 1rem 1.25rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-top: auto;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    font-size: 0.875rem;
    font-weight: 700;
    color: #1d2822;
    text-decoration: none;
    transition: color 0.2s ease;
    align-self: flex-start;
    margin-top: auto;
    padding-top: 0.75rem;

    &:hover {
      color: #9ddda9;
    }
  }

  // Variants
  &--compact {
    .social-media-card__header {
      padding: 0.75rem;
    }

    .social-media-card__content {
      padding: 0.75rem;
    }

    .social-media-card__footer {
      padding: 0.5rem 0.75rem;
    }
  }

  &--featured {
    .social-media-card__imageContainer {
      max-height: 25rem;
    }

    .social-media-card__title {
      font-size: 1.125rem;
    }
  }

  // Responsive
  @media (max-width: 768px) {
    &__header {
      padding: 0.75rem;
    }

    &__content {
      padding: 0.75rem;
    }

    &__footer {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }
  }
}
</style>
